import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Leaf } from 'lucide-react';

// Floating particles component
const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-[#D4AF37] rounded-full opacity-30 animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${8 + Math.random() * 4}s`,
          }}
        />
      ))}
    </div>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { width, height } = heroRef.current.getBoundingClientRect();
      const x = (clientX / width - 0.5) * 20;
      const y = (clientY / height - 0.5) * 20;
      heroRef.current.style.setProperty('--mouse-x', `${x}px`);
      heroRef.current.style.setProperty('--mouse-y', `${y}px`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={heroRef}
      data-testid="hero-section"
      className={`relative min-h-screen flex flex-col justify-center items-center overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0D0D0D]' : 'bg-[#FAF7F0]'
      }`}
    >
      {/* Animated gradient background */}
      <div 
        className={`absolute inset-0 transition-opacity duration-500 ${
          isDark 
            ? 'bg-gradient-to-br from-[#0D0D0D] via-[#1A1A1A] to-[#0D0D0D]' 
            : 'bg-gradient-to-br from-[#FAF7F0] via-[#F8F5EE] to-[#FAF7F0]'
        }`} 
      />
      
      {/* Animated radial glow that follows mouse */}
      <div 
        className={`absolute w-[1000px] h-[800px] rounded-full blur-[150px] pointer-events-none transition-all duration-1000 ease-out ${
          isDark ? 'bg-[#D4AF37]/8' : 'bg-[#D4AF37]/15'
        }`}
        style={{
          left: '50%',
          top: '40%',
          transform: 'translate(calc(-50% + var(--mouse-x, 0px)), calc(-50% + var(--mouse-y, 0px)))',
        }}
      />

      {/* Secondary glow */}
      <div 
        className={`absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none animate-pulse-slow ${
          isDark ? 'bg-[#D4AF37]/5' : 'bg-[#D4AF37]/10'
        }`}
      />

      {/* Floating golden particles */}
      {isDark && <FloatingParticles />}
      
      {/* Subtle noise texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Fine line pattern */}
      <div 
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(
            90deg,
            transparent,
            transparent 100px,
            ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(212,175,55,0.2)'} 100px,
            ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(212,175,55,0.2)'} 101px
          )`,
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
        {/* Electric Vehicle Badge */}
        <div 
          data-testid="ev-badge"
          className={`inline-flex items-center gap-2 mb-8 px-5 py-2.5 rounded-full animate-fade-in-up ${
            isDark 
              ? 'border border-[#D4AF37]/40 bg-[#D4AF37]/10 shadow-[0_0_30px_rgba(212,175,55,0.15)]' 
              : 'border border-[#D4AF37]/50 bg-[#D4AF37]/15 shadow-[0_0_30px_rgba(212,175,55,0.2)]'
          }`}
        >
          <Leaf className="w-4 h-4 text-[#D4AF37] animate-pulse" />
          <span className="text-xs tracking-widest uppercase text-[#D4AF37] font-medium">
            {t('hero.badge')}
          </span>
        </div>

        {/* Main Headline with golden shimmer effect */}
        <h1 
          data-testid="hero-headline"
          className={`font-playfair text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-tight mb-8 animate-fade-in-up delay-100 ${
            isDark ? 'text-white' : 'text-[#1A1A1A]'
          }`}
        >
          <span className="relative inline-block">
            {t('hero.headline').split('Vaud')[0]}
            <span className="relative">
              Vaud
              <span className="absolute inset-0 bg-gradient-to-r from-[#D4AF37] via-[#F5E6A3] to-[#D4AF37] bg-clip-text text-transparent animate-shimmer bg-[length:200%_100%]">
                Vaud
              </span>
            </span>
          </span>
        </h1>

        {/* Subheadline */}
        <p 
          data-testid="hero-subheadline"
          className={`text-base sm:text-lg lg:text-xl max-w-2xl mx-auto mb-14 leading-relaxed animate-fade-in-up delay-200 ${
            isDark ? 'text-[#F8F5EE]/70' : 'text-[#1A1A1A]/70'
          }`}
        >
          {t('hero.subheadline')}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 animate-fade-in-up delay-300">
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollToSection('contact')}
            className="w-full sm:w-auto bg-[#D4AF37] text-[#0A0A0A] px-12 py-4 text-sm font-semibold rounded-full hover:bg-[#F5E6A3] hover:shadow-[0_0_40px_rgba(212,175,55,0.6)] transition-all duration-400 transform hover:scale-[1.03]"
          >
            {t('hero.cta')}
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToSection('services')}
            className={`w-full sm:w-auto border-2 border-[#D4AF37]/60 px-12 py-4 text-sm font-medium rounded-full transition-all duration-400 ${
              isDark 
                ? 'text-[#D4AF37] hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]' 
                : 'text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]'
            }`}
          >
            {t('hero.ctaSecondary')}
          </button>
        </div>
      </div>

      {/* Decorative golden separator */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" className="w-full" preserveAspectRatio="none">
          <path
            d="M0,60 L0,30 Q360,0 720,30 Q1080,60 1440,30 L1440,60 Z"
            fill={isDark ? '#0D0D0D' : '#FAF7F0'}
            className="transition-colors duration-500"
          />
          <path
            d="M0,30 Q360,0 720,30 Q1080,60 1440,30"
            fill="none"
            stroke="#D4AF37"
            strokeWidth="2"
            strokeOpacity="0.3"
          />
        </svg>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 animate-bounce">
        <div className={`w-6 h-10 rounded-full flex items-start justify-center p-2 ${
          isDark ? 'border border-white/20' : 'border border-[#D4AF37]/40'
        }`}>
          <div className="w-1 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
